import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// src/i18n.ts

// src/i18n.ts (only replace formatCurrency; keep your resources + init)
export const formatCurrency = (
  amount: number | string | null | undefined,
  currency: string | null | undefined,
  lang: string | null | undefined,
) => {
  // Coerce amount safely
  const n = typeof amount === 'number' ? amount : Number(amount);
  if (!Number.isFinite(n)) return '';

  // Default currency if missing/invalid
  const code =
    typeof currency === 'string' && currency.trim()
      ? currency.trim().toUpperCase()
      : 'USD';

  // Map language -> locale
  let locale = 'en-US';
  const langCode = String(lang || 'en').split('-')[0];

  switch (langCode) {
    case 'es':
      locale = 'es-ES';
      break;
    case 'fr':
      locale = 'fr-FR';
      break;
    case 'ja':
      locale = 'ja-JP';
      break;
    case 'ko':
      locale = 'ko-KR';
      break;
    default:
      locale = 'en-US';
      break;
  }

  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: code,
      currencyDisplay: 'symbol',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(n);
  } catch (e) {
    // If code is STILL invalid (e.g. "DOLLARS"), Intl throws — never crash Bazaar
    const prefix = code === 'USD' ? '$' : `${code} `;
    return `${prefix}${n.toFixed(2)}`;
  }
};


export const RODDO_SUPPORTED_LANGUAGE_CODES = ["en", "es", "ko"] as const;
export type RoddoSupportedLanguage = (typeof RODDO_SUPPORTED_LANGUAGE_CODES)[number];

export const RODDO_LANGUAGE_LABELS: Record<RoddoSupportedLanguage, string> = {
  en: "English",
  es: "Español",
  ko: "한국어",
};

export const normalizeRoddoLanguageCode = (value: unknown): RoddoSupportedLanguage => {
  const code = String(value || "")
    .trim()
    .toLowerCase()
    .split("-")[0];

  return (RODDO_SUPPORTED_LANGUAGE_CODES as readonly string[]).includes(code)
    ? (code as RoddoSupportedLanguage)
    : "en";
};

export const setRoddoLanguage = async (value: unknown) => {
  const code = normalizeRoddoLanguageCode(value);
  await i18n.changeLanguage(code);

  try {
    window.localStorage.setItem("roddo_language", code);
    window.localStorage.setItem("i18nextLng", code);
    document.documentElement.lang = code;
  } catch {}

  return code;
};

const resources = {
  en: {
    translation: {
      // General
      app: {
        loading: 'Loading...',
        announcementSent: 'Announcement sent to all users',
        purchaseComplete: 'Purchase complete!',
        userDeleted: 'User {{name}} and all their content has been removed.',
        catchDeleted: 'Catch removed from your diary.',
        commentDeleted: 'Comment deleted.',
        postDeleted: 'Post has been deleted.',
        listingRemoved: 'Listing has been removed from Baleen Bay Bazaar.',
        offerSent: 'Offer sent successfully!',
        offerAccepted: 'Offer accepted! The item is now marked as sold.',
        offerDeclined: 'Offer declined.',
        counterOfferSent: 'Counter-offer sent.',
        licenseAdded: '{{state}} license added to your profile!',
        setupComplete: 'Setup complete! Welcome to Roddo.',
        goToPurchaseLicense: 'Lets find you a license!',
        publicSpotAdded: 'New public spot shared with the community!',
        privateSpotSaved: 'Private spot saved successfully.',
        upgradedToPro: 'Welcome to Pro! You have unlocked all premium features.',
        tournamentUpdated: 'Tournament details have been updated.',
        tournamentCreated: 'New tournament has been created!',
        winnersDetermined: 'Winners have been determined and notified.',
        catchAddedToDiary: 'Catch added to your diary!',
        itemAlreadyInDiary: 'This catch is already in your diary.',
        itemWithoutImageDiary: 'Only posts with an image can be added to your diary.',
        joinedTournament: 'You have joined the tournament!',
        confirmDeletePost: 'This will permanently remove the post and its comments. This action cannot be undone.',
        confirmDeleteListing: 'This will permanently remove your listing from the marketplace. This action cannot be undone.',
        confirmDeleteCatch: 'Are you sure you want to delete your {{weight}} {{species}} catch? This action cannot be undone.',
        forecastError: 'Could not build a fishing forecast yet. Please try again later.',
        locationDenied: 'Location denied. Using default location for forecast.',
        catchLoggedAndShared: 'Catch logged & shared!',
        postShared: 'Your post has been shared!',
        itemListedSuccess: 'Your item has been listed in the Bazaar!',
      },
      notifications: {
        title: 'Notifications',
        empty: "You're all caught up!",
        markAllRead: 'Mark all as read',
        clearAll: 'Clear All',
        primeTime: 'Prime time fishing alert! Score is {{score}} for {{species}}.',
        licenseExpiring: 'Your {{state}} license expires on {{date}}.',
        eventNearby: 'Community event "{{eventName}}" is happening near you!',
        restrictedZone: 'Alert: You have entered a restricted zone: {{zoneName}} ({{reason}}).',
        challengeComplete: 'Challenge Complete! You earned {{points}} XP for "{{title}}".',
        levelUp: 'Leveled Up! You are now Level {{level}}.',
        newOffer: 'You received a new offer of {{amount}} from {{name}} for "{{itemName}}".',
        offerAccepted: 'Your offer for "{{itemName}}" was accepted!',
        offerDeclined: 'Your offer for "{{itemName}}" was declined.',
        offerDeclinedSold: 'An offer for "{{itemName}}" was accepted, so your offer was declined.',
        offerCountered: 'You received a counter-offer of {{amount}} for "{{itemName}}".',
        tournamentAnnouncement: 'New Tournament: "{{name}}"! Prize pool of {{prize}}. Join now!',
      },
      // Views / Titles
      views: {
        Home: 'Tackle Box',
        Map: 'Home',
        Gadgets: 'Gadgets',
        Community: 'Community',
        Bazaar: 'Baleen Bay Bazaar',
        Events: 'Events',
        Profile: 'My Page',
        LogCatch: 'Log',
        Admin: 'Super Secret Panel',
        EditProfile: 'Edit My Info',
        SellerDashboard: 'Seller Hub',
        AR: 'Scan Catch',
        Challenges: 'Challenges',
      },
      // Common UI
      common: {
        save: 'Save',
        cancel: 'Cancel',
        delete: 'Delete',
        edit: 'Edit',
        confirm: 'Confirm',
        submit: 'Submit',
        search: 'Search',
        close: 'Close',
        back: 'Back',
        next: 'Next',
        finish: 'Finish',
        skip: 'Skip for now',
        buy: 'Buy',
        buying: 'Buying',
        open: 'Open',
        hide: 'Hide',
        saving: 'Saving…',
        offer: 'Offer',
        accept: 'Accept',
        decline: 'Decline',
        counter: 'Counter',
        loadMore: 'Load More',
        saveChanges: 'Save Changes',
        showAll: 'Show All',
        showLess: 'Show Less',
      },
      // Header
      header: {
        notifications: 'Notifications',
      },
      // Home View
      home: {
        goodMorning: 'Good morning',
        goodAfternoon: 'Good afternoon',
        goodEvening: 'Good evening',
        logCatch: 'Log a Catch',
        quickLogTitle: 'Quick Log a Catch',
        scanCatch: 'Scan a Catch',
        secretSpots: 'Secret Spots',
        seeCommunity: 'See Community',
        licenseStatus: '{{state}} License',
        licenseExpires: 'Expires: {{expiry}}',
        viewDetails: 'View Details',
        hideDetails: 'Hide Details',
        noLicensePrompt: 'Stay Compliant!',
        noLicenseMessage: 'Add or purchase a fishing license to stay on the water.',
        manageLicenses: 'Manage Licenses',
        newbieTab: 'Tips & Tricks',
        newbieTitle: "A Beginner's Guide to Fishing",
        newbieSubtitle: "Your quick-start guide to a lifetime of fishing adventures, based on FWS guidelines.",
        newbieSectionBeforeYouGoTitle: '1. Before You Go: License & Regs',
        newbieSectionBeforeYouGoText: "Almost everyone 16 and older needs a fishing license! It's how we help protect our fish populations. Also, be sure to check the local fishing regulations for the area you plan to fish.",
        newbieSectionGearTitle: '2. Essential Beginner Gear',
        newbieSectionGearSubtitle: "You don't need a lot to get started! Here are the basics:",
        newbieSectionGear1: 'Rod and Reel Combo: A medium-action spinning combo is a versatile choice for beginners.',
        newbieSectionGear2: 'Fishing Line: 6-10 pound test monofilament line is a great all-purpose option.',
        newbieSectionGear3: 'Hooks: A variety pack of hooks in sizes 6-10 will cover most situations.',
        newbieSectionGear4: 'Bait or Lures: Live worms are a can\'t-miss for most freshwater fish. Small spinners or spoons are good artificial options.',
        newbieSectionGear5: 'Bobbers (or Floaters): They help you know when you have a bite!',
        newbieSectionGear6: 'Sinkers (or Weights): These help your bait get down to where the fish are.',
        newbieSectionGearAlso: "Also consider bringing needle-nose pliers, a first-aid kit, sunscreen, and sunglasses.",
        newbieSectionRigTitle: '3. Setting Up Your Rig',
        newbieSectionRigKnotSubtitle: "The Improved Clinch Knot",
        newbieSection2Subtitle: "The Improved Clinch Knot is a must-know. It's strong and reliable for tying on lures, hooks, and swivels.",
        newbieSectionRigBobberSubtitle: "Simple Bobber Rig",
        newbieSectionRigBobberStep1: 'Attach your hook to the line using an Improved Clinch Knot.',
        newbieSectionRigBobberStep2: 'Attach a sinker (split-shot) about 6-12 inches above the hook.',
        newbieSectionRigBobberStep3: 'Attach a bobber to your line, anywhere from 1-3 feet above the sinker. You can adjust its position to change how deep your bait goes.',
        newbieSectionCastTitle: '4. Mastering the Cast',
        newbieSection3Subtitle: 'Casting is the first skill to master. Here\'s a simple overhead cast for spinning reels:',
        newbieSection3Step1: "Hold the rod with your dominant hand, with the reel's stem between your middle and ring fingers.",
        newbieSection3Step2: 'Let about 6-12 inches of line hang from the rod tip. Pinch the line against the rod with your index finger.',
        newbieSection3Step3: 'Open the bail (the metal arm on the reel).',
        newbieSection3Step4: "Bring the rod tip back over your shoulder to about the 2 o'clock position.",
        newbieSection3Step5: "In a smooth, forward motion, bring the rod forward. Release your index finger from the line when the rod is at about the 10 o'clock position to send the lure flying.",
        newbieSectionBiteTitle: "5. You've Got a Bite! Now What?",
        newbieSectionBiteSubtitle: "Feeling a tug or seeing your bobber go under is a thrill! Here's what to do next:",
        newbieSectionBiteStep1: 'Set the Hook: Give a quick, firm (but not violent!) upward sweep of your rod. This drives the hook into the fish\'s mouth.',
        newbieSectionBiteStep2: 'Reel it In: Keep the rod tip up and maintain steady pressure on the line. Reel in smoothly. If the fish pulls hard, let it run a little (this is called "playing" the fish) before reeling again.',
        newbieSectionBiteStep3: 'Land It: Guide the fish toward the shore or your boat and use a net or your hands to lift it from the water.',
        newbieSectionHandleTitle: '6. Handling Your Catch Safely',
        newbieSectionHandleSubtitle: "Whether you're keeping it or releasing it, proper handling is key for the fish's well-being and your safety.",
        newbieSectionHandleStep1: 'Wet Your Hands: Before touching a fish, always wet your hands to protect its sensitive slime coat.',
        newbieSectionHandleStep2: 'Support the Body: Hold the fish horizontally, supporting its belly with one hand and gripping its lower jaw or the area in front of its tail with the other.',
        newbieSectionHandleStep3: 'Quick Release: If you are not keeping the fish, return it to the water as quickly as possible. Gently place it back in the water and let it swim away on its own.',
        newbieSectionEthicsTitle: "7. Angler's Ethics: Leave No Trace",
        newbieSectionEthicsSubtitle: "A good angler is also a good steward of the environment. Always follow the Leave No Trace principles.",
        newbieSectionEthicsStep1: "Pack It In, Pack It Out: Take all of your trash with you, including food wrappers, containers, and especially tangled fishing line, which can be very harmful to wildlife.",
        newbieSectionEthicsStep2: "Fish Responsibly: Obey all fishing regulations, including size and catch limits.",
        newbieSectionEthicsStep3: "Leave It Better: Leave your fishing spot cleaner than you found it. Pick up any trash you see, even if it isn't yours."
      },
      // AR View
      ar: {
        analyzing: 'Analyzing your catch...',
        error: "Couldn't identify the fish. Please try again.",
        notAFish: "That doesn't look like a fish! Try another angle.",
        cameraError: 'Could not access camera. Please check permissions.',
        tryAgain: 'Try Again',
        itsA: "IT'S A...",
        scanAnother: 'Scan Another',
        logThisCatch: 'Log This Catch!',
      },
      // Map View
      map: {
        searchPlaceholder: 'Search for a location...',
        searchError: 'Location not found.',
        searchFailed: 'Failed to search for location.',
        redoSearch: 'Search this area',
        finding: 'Finding...',
        hotspotError: 'Could not refresh this water read yet. Please try again.',
        offlineMaps: 'Offline Maps',
        downloadCurrent: 'Download Current Map View',
        myRegions: 'My Saved Regions',
        noOffline: 'No offline maps downloaded.',
        deleteRegionConfirm: 'Are you sure you want to delete the offline map "{{name}}"?',
        enterRegionName: 'Enter a name for this offline region:',
        defaultRegionName: 'My Fishing Trip',
        downloading: 'Downloading "{{name}}"...',
        tiles: 'tiles',
        tilesSaved: '{{count}} tiles saved',
        userLocationPopup: 'You are here!',
        tip: 'Tip',
        fact: 'Fact',
        zoomInError: 'Please zoom in further to search for hotspots.',
      },
      // Profile View
      profile: {
        level: 'Level',
        xp: 'XP',
        xpToNext: '{{count}} XP to Level {{level}}',
        adminPanel: 'Admin Panel',
        sellerHub: 'Seller Hub',
        myGadgets: 'My Gadgets',
        gadgetsSubtitle: 'Connect sonars & cameras',
        myBadges: 'My Badges',
        earned: 'Earned',
        moreBadges: '{{count}} more badges',
        noBadges: 'Start logging catches to earn badges!',
        myLicenses: 'My Fishing Licenses',
        manageLicenses: 'Add or Find a License',
        myTackleBox: 'My Tackle Box',
        addGear: '+ Add Gear',
        noTackle: 'Your tackle box is empty.',
        myCatchDiary: 'My Catch Diary',
        addNew: '+ Add New',
        noCatches: 'Your catch diary is empty!',
        noCatchesPrompt: 'Use the "Log a Catch" button to start your diary.',
        overview: 'Overview',
        analytics: 'Analytics',
        joined: 'Joined {{date}}',
        catches: 'Catches',
        species: 'Species',
        spots: 'Spots',
        confirmDeleteTackle: 'Are you sure you want to remove this item from your tackle box?',
        licenseModal: {
          title: 'Manage Fishing Licenses',
          addExisting: 'Add Existing License',
          findOnline: 'Find & Buy Online',
          addDetailsPrompt: 'Enter your existing license details.',
          stateLabel: 'State',
          statePlaceholder: 'e.g., California',
          numberLabel: 'License Number',
          numberPlaceholder: 'e.g., C1234567',
          expiryLabel: 'Expiry Date',
          saveButton: 'Save License',
          addError: 'Please fill out all license fields.',
          detectingLocation: 'Detecting your location...',
          placeholder: 'Enter any U.S. State...',
          suggested: '📍 Suggested based on your location.',
          error: 'Could not find a license website for "{{state}}". Please check the spelling or try another state.',
          officialSiteFor: 'Official License Website for {{state}}',
          redirectNotice: 'You will be redirected to an external government website to complete your purchase.',
          proceedTo: 'Proceed to {{state}} Website',
          externalSiteDisclaimer: 'Roddo uses the official NOAA directory for license information. All purchases are handled by state agencies.',
          noaaDirectory: 'NOAA directory',
          takePhoto: 'Take Photo',
          uploadImage: 'Upload Image',
        },
        tackleModal: {
          editTitle: 'Edit Gear',
          addTitle: 'Add New Gear',
          namePlaceholder: 'Item Name (e.g., St. Croix Mojo Bass)',
          brandPlaceholder: 'Brand (e.g., St. Croix)',
          modelPlaceholder: 'Model (e.g., MJC71MHF)',
          error: 'Name and category are required.',
        },
        tacklebox: {
          rods: "Rods",
          reels: "Reels",
          lures: "Lures",
          baits: "Baits",
          other: "Other Gear",
          add: "Add",
          noRods: "No rods added.",
          noReels: "No reels added.",
          noLures: "No lures added.",
          noBaits: "No baits added.",
          noOther: "No other gear added.",
          itemCount_one: "{{count}} item",
          itemCount_other: "{{count}} items",
          equipped: "Equipped",
          equip: "Equip",
          noneSelected: "None Selected",
        }
      },
      // Log Catch View
      logCatch: {
        title: 'Tell me about your catch!',
        photoLabel: 'Photo of your catch',
        uploadPhoto: 'Upload a Photo!',
        imageError: 'There was an error processing your image. Please try another one.',
        speciesLabel: 'What kind of fish was it?',
        speciesPlaceholder: 'e.g., Largemouth Bass',
        weightLabel: 'How much did it weigh?',
        weightPlaceholder: 'e.g., 5.2 lbs',
        date: 'Date',
        time: 'Time',
        locationLabel: 'Where did you catch it?',
        locationPlaceholder: 'e.g., Lake Harmony, PA',
        useCurrentLocation: 'Use current location',
        selectOnMap: 'Select on map',
        locationError: 'Could not get location. Please enable permissions.',
        lureBaitLabel: 'Lure / Bait Used',
        lureBaitPlaceholder: 'e.g., Topwater frog or Nightcrawler',
        techniqueLabel: 'Fishing Technique',
        techniquePlaceholder: 'e.g., Drop shot',
        saveButton: 'Save & Share',
        saving: 'Saving...',
        formError: 'Please fill out all required fields and upload a photo!',
        saveError: 'Could not save your catch yet. Please try again.',
        mapModalPrompt: 'Drag marker or tap map to select location',
        confirmLocation: 'Confirm Location',
      },
      chat: {
        placeholder: 'Escribe un mensaje...',
      },
      analytics: {
        title: 'My Analytics',
        subtitle: 'Discover patterns in your fishing success.',
        personalAITitle: 'Personal Roddo Insights',
        unlockAITitle: 'Unlock Roddo Insights!',
        unlockAISubtitle: 'Log at least 5 catches to unlock personalized fishing tips.',
        analyzing: 'Analyzing your catches...',
        insightsError: 'Could not build insights yet. Please try again later.',
        generateButton: 'Generate My Insights',
        forecastTitle: 'Fishing Forecast',
        generatingForecast: 'Generating...',
        forecastError: 'Could not build a forecast yet. Please try again later.',
        locationError: 'Could not get location for forecast.',
        tactic: 'Tactic',
        monthlyCatchRate: 'Monthly Catch Rate',
        speciesDistribution: 'Species Distribution',
        noDataTitle: 'No Data Yet!',
        noDataSubtitle: 'Log your catches to see your stats here.',
      },
      bazaar: {
        deleteTitle: 'Delete Listing'
      },
      community: {
        forum: 'Community',
        market: 'Baleen Bay Bazaar',
        events: 'Events',
        leaderboard: 'Leaderboard',
        searchPosts: 'Search posts...',
        searchMarket: 'Search Baleen Bay Bazaar...',
        searchUsers: 'Search users...',
        searchTournaments: 'Search tournaments...',
        allPosts: 'All Posts',
        following: 'Following',
        createPostPlaceholder: "What's on your mind, angler?",
        uploadPhoto: 'Add a Photo (Optional)',
        sharePost: 'Share Post',
        posting: 'Posting...',
        loadingPosts: 'Loading posts...',
        noPosts: 'No posts yet. Start the first dockside note.',
        editPost: 'Edit Post',
        deletePost: 'Delete Post',
        reportContent: 'Report Content',
        messageAuthor: 'Message Author',
        like: 'like',
        likes: 'likes',
        addToDiary: 'Add to Catch Diary',
        viewComments: 'View all {{count}} comments',
        noComments: 'No comments yet.',
        addCommentPlaceholder: 'Add a comment...',
        listItem: 'List an Item',
        emptyBazaarTitle: "The Bazaar is Quiet",
        emptyBazaarMessage: "There are no items for sale right now. Why not list something?",
        yourOffer: 'Your offer: {{amount}} ({{status}})',
        buyNow: 'Buy Now',
        makeOffer: 'Make Offer',
        likedBy: 'Liked By',
        noLikesYet: 'No likes yet.',
        editModalTitle: 'Edit your post',
        addItemModalTitle: 'List Item in Bazaar',
        itemName: 'Item Name',
        price: 'Price',
        description: 'Description',
        addPhoto: 'Add Photo',
        listing: 'Listing...',
        offerModalTitle: 'Make an Offer',
        offerFor: 'for',
        asking: 'is asking',
        yourOfferPlaceholder: 'Your offer (e.g., {{amount}})',
        reportToast: 'Content has been reported for review.',
        copyToast: 'Link copied to clipboard!',
        confirmReportPost: 'Are you sure you want to report this post for inappropriate content?',
        confirmDeleteComment: 'Are you sure you want to delete this comment?',
        alert: {
          imageError: 'There was an error processing your image.',
          captionRequired: 'Please add a caption!',
          postError: 'Failed to create post. Please try again.',
          chatSoon: 'Direct messaging will be available soon!',
          itemDetails: 'Please fill in a name, price, and add an image.',
          validPrice: 'Please enter a valid price.',
          validOffer: 'Please enter a valid offer amount.'
        }
      },
      events: {
        noTournaments: "No tournaments are active or upcoming right now.",
        upcoming: "Upcoming",
        active: "Active",
        completed: "Completed",
        prizePool: "Prize Pool",
        starts: "Starts",
        ends: "Ends",
        joined: "Joined",
        join: "Join Tournament",
        hideDetails: "Hide Details",
        viewDetails: "View Details",
        rules: "Rules",
        leaderboard: "Leaderboard",
        noCatches: "No catches submitted yet.",
        winners: "🏆 Winners 🏆"
      },
      leaderboard: {
        level: 'Level',
        xp: 'XP',
      },
      payment: {
        title: 'Complete Your Purchase',
        purchasing: 'You are purchasing:',
        selectMethod: 'Select Payment Method',
        card: 'Credit/Debit Card',
        secure: 'Secure SSL Encrypted Payment',
        payButton: 'Pay {{amount}}',
        processing: 'Processing payment...',
        successTitle: 'Payment Successful!',
        successSubtitle: 'Your purchase is complete.',
        failedTitle: 'Payment Failed',
        failedSubtitle: 'Please try another payment method.',
      },
      licenseDetail: {
        title: 'Official Fishing License',
        holder: 'HOLDER',
        type: 'TYPE',
        licenseNo: 'LICENSE #',
        expires: 'EXPIRES',
        verification: 'This is a digital representation. Always carry your official license if required by law.'
      },
      seller: {
        title: "Seller's Hub",
        subtitle: "Manage your listings and sales.",
        activeListings: "Active Listings",
        totalSales: "Total Sales",
        myListings: "My Listings",
        sold: "SOLD",
        available: "Available",
        pendingOffers: "Pending Offers",
        offered: "offered",
        noListings: "You have no items listed for sale.",
        listPrompt: "Tap 'List an Item' in the community tab to get started.",
        editTitle: "Edit Listing",
        counterTitle: "Make a Counter-Offer",
        buyer: "Buyer",
        deleteTitle: 'Delete Listing'
      },
      devices: {
        title: "My Gadgets",
        subtitle: "Connect and manage your smart fishing devices.",
        connected: "Connected Devices",
        noneConnected: "No devices are connected.",
        scanPrompt: "Scan for nearby devices to get started.",
        findNew: "Find New Devices",
        scan: "Scan for Devices",
        scanning: "Scanning...",
        found: "{{count}} devices found",
        connect: "Connect",
        disconnect: "Disconnect",
        liveFeed: "Live Feed",
        sonars: "Sonars",
        cameras: "Cameras",
        drones: "Drones",
        feedTitle: "Live Feed: {{name}}",
        status: "Status: ",
        connectedStatus: "Connected"
      },
      gear: {
        hero: {
          title: 'Your fishing loadout',
          subtitle: 'Tune rods, reels, lures, line, and smart gear before the next trip. The Hold keeps the kit organized and ready.',
        },
        manage: 'Manage',
        addGear: '✨ Add gear',
        totalGear: 'Total gear',
        loadoutScore: 'Loadout',
        mostUsed: 'Most used',
        heaviestLure: 'Heaviest lure',
        none: 'None yet',
        equipped: 'Equipped',
        equip: 'Equip',
        activeKit: 'Active kit',
        savedCount: '{{count}} saved',
        editGear: 'Edit gear',
        tabs: {
          rod: 'Rods',
          reel: 'Reels',
          baitsLures: 'Baits',
          line: 'Line',
          smart: 'Smart',
          other: 'Other',
        },
        categories: {
          rod: 'Rod',
          reel: 'Reel',
          lure: 'Lure',
          bait: 'Bait',
          line: 'Line',
          smart: 'Smart',
          gear: 'Gear',
        },
        fields: {
          name: 'Name',
          brand: 'Brand',
          model: 'Model',
          weightOz: 'Weight (oz)',
          value: 'Value',
          notes: 'Notes',
        },
        delete: {
          title: 'Delete gear item?',
          confirm: 'This permanently removes the saved gear item from The Hold.',
        },
      },
      gearHub: {
        title: 'The Hold',
        subtitle: 'Gear, licenses, and prep in one place.',
        kitShelf: '🧃 Kit shelf',
        addGear: '✨ Add gear',
        empty: 'Nothing here yet.',
        emptyHint: 'Tap ✨ Add gear to save your next piece.',
        manageTackle: 'Manage Tackle Box',
        equipped: 'Equipped',
      },
      tackleBox: {
        title: 'Tackle Box',
        rods: 'Rods',
        reels: 'Reels',
        lures: 'Lures',
        baits: 'Baits',
        line: 'Line',
        other: 'Other gear',
        addGear: 'Add gear',
        noGear: 'No gear yet.',
        tabs: {
          loadout: 'Loadout',
          stats: 'Stats',
          avatar: 'Avatar',
        },
        gear: {
          title: 'Equipped',
          none: 'None equipped',
          manage: 'Manage all gear',
          add: 'Add gear',
          equipSuccess: 'Equipped.',
          clearSuccess: 'Equipped gear cleared.',
        },
        avatar: {
          shellWallet: 'Shell Wallet',
          shellWalletCopy: 'Available cosmetic currency for avatar gear. Earn more through logging, Roddo Reads, challenges, and rewards.',
          lifetimeEarned: 'Lifetime earned',
          avatarGear: 'Avatar gear',
          profileLoadout: 'Profile loadout',
          profileLoadoutCopy: 'Manage your Angler Card cosmetics. Shell Shop buys stay cosmetic-only.',
          equipped: 'Equipped',
          starter: 'Starter',
          unequip: 'Unequip',
          equip: 'Equip',
          locked: 'Locked',
          shellShopPreview: 'Shell Shop preview',
          shells: 'Shells',
          tuckedAway: 'Avatar loadout tucked away',
          hiddenSummary: '{{equipped}} equipped · {{owned}} owned cosmetics',
          tapToOpen: 'Tap to open',
        },
        deck: {
          label: 'Equipment deck',
          title: 'Active kit + inventory',
          copy: 'Saved tackle up top. Equipped loadout below.',
          activeRod: 'Active rod',
          activeReel: 'Active reel',
          activeLure: 'Active lure',
        },
        licenses: {
          manage: 'Manage licenses',
          viewPhoto: 'View photo',
        },
      },
      welcome: {
        firstCast: 'Roddo Pilot',
        title: 'Welcome to Roddo Pilot',
        subtitle: 'Thanks for helping shape Roddo early.',
        tourTitle: 'Roddo Pilot Tour',
        tourSubtitle: 'Six quick stops. Then you are fishing.',
        skip: 'Skip',
        close: 'Close',
        step: 'Step {{step}} of {{total}}',

        pilotEyebrow: 'You are early',
        pilotTitle: 'Welcome, Pilot Angler',
        pilotKicker: 'Early access',
        pilotDesc: 'Thanks for helping shape Roddo early. Your feedback helps us build a better fishing app for real anglers.',
        pilotAccent: 'Pilot users earn early perks.',

        startHere: 'Start here',
        todayBitePlan: "Today's Bite Plan",
        dailyLoop: 'Your daily fishing plan',
        biteDesc: 'Not sure where to start? Roddo gives you a simple plan for the day: check the water, see what might be biting, and log what you learn.',
        biteAccent: 'A simple fishing game plan.',

        scoutWater: 'Scout water',
        map: 'Fishing map',
        mapDesc: 'Big Blue helps you explore nearby water, save places you care about, and get a better feel for the area before you go.',
        mapAccent: 'Find water without guessing.',

        readWater: 'Read the water',
        waterClues: 'Water clues',
        fishiqDesc: 'Roddo Reads helps you read the water with local clues about species, habitat, timing, and tactics.',
        fishiqAccent: 'Better clues. Less guessing.',

        coreLoop: 'Save catches',
        logCatch: 'Your fishing memory',
        logDesc: 'Log catches, photos, notes, weather, and water details so every trip teaches you something for next time.',
        logAccent: 'Every catch has a story.',

        optionalSocial: 'Find your crew',
        community: 'Community',
        bayDesc: 'Baleen Bay is where anglers post catches, ask questions, share tips, and keep fishing talk organized by topic.',
        bayAccent: 'Your dock for fishing talk.',

        yourRecords: 'Your setup',
        tackleBox: 'Tackle box',
        holdDesc: 'Keep your rods, reels, lures, and gear in one place so you can remember what you used and what worked.',
        holdAccent: 'Track the gear that works.',

        bitePlan: 'Bite Plan',
        bitePlanControl: 'Start with a simple plan.',
        recenter: 'Recenter',
        recenterCopy: 'Jump back to your location.',
        fishiqControl: 'See local fishing clues.',
        favorites: 'Favorites',
        favoritesCopy: 'Save water you like.',

        ready: 'Ready to roll',
        startBitePlan: "Start with today's Bite Plan?",
        fastestLoop: 'The easiest Roddo loop is simple: make a plan, scout Big Blue, check Roddo Reads, then log what happened.',
        reopenTour: 'You can reopen this tour later in Settings.',
        openBitePlan: 'Open Bite Plan',
        scoutBigBlue: 'Scout Big Blue',
        logFirstCatch: 'Log First Catch',
        exploreAnytime: 'You can explore Big Blue or log your first catch anytime.',
        tileEarlyAccess: 'Early Access',
        tileBitePlan: 'Bite Plan',
        tileBigBlue: 'Big Blue',
        tileFishIQ: 'Roddo Reads',
        tileLog: "Capt'n's Log",
      }
    }
  },
  es: {
    translation: {
      app: {
        loading: 'Cargando...',
      },
      views: {
        Home: 'The Hold',
        Map: 'Big Blue',
        Gadgets: 'Dispositivos',
        Community: 'Baleen Bay',
        Bazaar: 'Baleen Bay Bazaar',
        Events: 'Eventos',
        Profile: 'Mi página',
        LogCatch: 'Capt’n’s Log',
        EditProfile: 'Editar perfil',
        Challenges: 'Retos',
      },
      common: {
        save: 'Guardar',
        cancel: 'Cancelar',
        delete: 'Eliminar',
        edit: 'Editar',
        confirm: 'Confirmar',
        submit: 'Enviar',
        search: 'Buscar',
        close: 'Cerrar',
        back: 'Atrás',
        next: 'Siguiente',
        finish: 'Terminar',
        skip: 'Omitir por ahora',
        done: 'Listo',
        view: 'Ver',
        open: 'Abrir',
        hide: 'Ocultar',
        buy: 'Comprar',
        buying: 'Comprando',
        copyLink: 'Copiar enlace',
        saveChanges: 'Guardar cambios',
        saved: 'Guardado',
        saving: 'Guardando…',
        accepted: 'Aceptado',
        missing: 'Falta',
        version: 'Versión',
        acceptedAt: 'Aceptado {{date}}',
        notRecorded: 'No registrado',
        step: 'Paso {{step}} de {{total}}',
      },
      settings: {
        title: 'Configuración',
        subtitle: 'Ajusta Roddo para tu forma de pescar.',
        account: 'Cuenta',
        preferences: 'Preferencias',
        legal: 'Legal y privacidad',
        support: 'Soporte',
      },
      bottomNav: {
        home: 'The Hold',
        map: 'Big Blue',
        community: 'Baleen Bay',
        log: 'Log',
        profile: 'Perfil',
      },
      editProfile: {
        title: 'Editar perfil',
        subtitle: 'Mantén tu identidad de Roddo clara y lista.',
        language: 'Idioma',
        languageHelp: 'Cambia el idioma principal de Roddo.',
        noChanges: 'Sin cambios',
        saveChanges: 'Guardar cambios',
      },
      onboarding: {
        validEmail: 'Ingresa un correo válido.',
        requestFailed: 'No se pudo enviar la solicitud. Inténtalo de nuevo.',
        requestSubmitted: 'Solicitud enviada. Te contactaremos si se aprueba.',
        requestWatchEmail: 'Solicitud enviada. Revisa tu correo.',
        licenseFields: 'Completa todos los campos de la licencia.',
        licenseAdded: 'Licencia agregada.',
        licenseNotConfigured: 'El escaneo de licencia aún no está configurado. Puedes ingresar los datos manualmente.',
        scanNoDetails: 'No pudimos extraer los datos. Prueba con una foto más clara o ingrésalos manualmente.',
        scanFailed: 'No se pudo escanear la licencia. Ingresa los datos manualmente.',
        accessGateway: 'Puerta de acceso',
        inviteCode: 'Código de invitación',
        verifyInvite: 'Verificar invitación',
        checking: 'Revisando…',
        accessVerified: 'Invitación verificada. Bienvenido a bordo.',
        noCode: '¿Sin código?',
        requestAccessCopy: 'Solicita acceso y te contactaremos cuando haya un lugar disponible.',
        tellUsWho: 'Cuéntanos quién eres',
        requestAccess: 'Solicitar acceso',
        continue: 'Continuar',
        firstRun: 'Primer recorrido de Roddo',
        welcomeTitle: 'Bienvenido a tu mundo de pesca',
        welcomeCopy: 'Roddo te ayuda a explorar Big Blue, leer señales útiles de Roddo Reads, registrar lo que pasó, organizar The Hold y convertir salidas reales en progreso.',
        startBigBlueTitle: 'Empieza en Big Blue',
        startBigBlueCopy: 'Abre el mapa del mundo, revisa el agua cercana, el clima y tus puntos guardados antes de elegir un plan.',
        fishiqGuideTitle: 'Usa Roddo Reads como guía',
        fishiqGuideCopy: 'Roddo Reads sugiere especies probables, horarios y condiciones: señales útiles, nunca una garantía mágica.',
        logHappenedTitle: 'Registra lo que realmente pasó',
        logHappenedCopy: 'Capt’n’s Log convierte capturas, días sin pique, equipo y notas del agua en XP, Shells ganadas y mejores lecturas futuras.',
        simpleLoop: 'Bucle simple: explora un agua → consulta Roddo Reads → pesca → registra una nota honesta → gana progreso acogedor → vuelve más listo la próxima vez.',
        showLoop: 'Muéstrame el ciclo',
        inviteRequired: 'Se requiere invitación para continuar.',
        buildCardTitle: 'Crea tu tarjeta de pescador',
        buildCardCopy: 'Mantenlo simple. Así Roddo empieza a sentirse como tu hogar de pesca.',
        displayName: 'Nombre visible',
        displayNamePlaceholder: 'ej. Capitán Jack',
        bio: 'Bio',
        optional: 'opcional',
        bioPlaceholder: 'Comparte tu estilo de pesca, aguas favoritas, especies objetivo o lo que estás aprendiendo...',
        licenseVaultTitle: 'Bóveda de licencias',
        licenseVaultCopy: 'Opcional para configurar. Útil después cuando la necesites rápido.',
        addLicense: 'Agregar licencia a The Hold',
        findLicense: 'Buscar información de licencia',
        takePhoto: 'Tomar foto',
        upload: 'Subir',
        readingLicense: 'Leyendo foto de licencia...',
        state: 'Estado',
        statePlaceholder: 'ej. California',
        licenseNumber: 'Número de licencia',
        licenseNumberPlaceholder: 'ej. C1234567',
        expiryDate: 'Fecha de vencimiento',
        saveContinue: 'Guardar y continuar',
        bitePlanTitle: 'Tu primer Bite Plan',
        bitePlanCopy: 'Empieza con el ciclo simple de Roddo. Sin tarea, sin humo: solo un camino claro desde “¿dónde pesco?” hasta “¿qué aprendí?”',
        firstMove: 'Primer paso: toca Big Blue.',
        firstMoveCopy: 'No necesitas una captura perfecta para comenzar. Una nota de agua, un reporte sin pique o una elección de equipo basta para que Roddo empiece a aprender contigo — y las Shells solo crecen con progreso ganado.',
        enterBigBlue: 'Entrar a Big Blue',
        nextStop: 'La próxima parada es Big Blue. Tu billetera de Shells vive en The Hold cuando quieras revisar recompensas de progreso.',
        planBigBlueCopy: 'Empieza en el mapa. Elige un agua y revisa las condiciones.',
        planFishIQCopy: 'Elige una especie probable o una pista de horario. Trátalo como una corazonada inteligente.',
        planLogCopy: 'Guarda capturas, días sin pique, equipo, claridad, carnada o una observación rápida.',
        planHoldCopy: 'Mantén licencias, equipo, identidad de avatar, billetera de Shells y preparación en un solo lugar.',
        planShellsCopy: 'Se ganan con progreso de Roddo. Recompensas acogedoras para avatar — no dinero ni fondos de mercado.',
        planRoddodexCopy: 'Tu conocimiento de peces crece con registros reales y especies exploradas.',
        planBayCopy: 'Comparte señales útiles y pregunta cuando quieras ayuda de la comunidad.',
      },
      welcome: {
        firstCast: 'Roddo Pilot',
        title: 'Bienvenido a Roddo Pilot',
        subtitle: 'Gracias por ayudar a darle forma a Roddo desde temprano.',
        tourTitle: 'Tour de Roddo Pilot',
        tourSubtitle: 'Seis paradas rápidas. Luego a pescar.',
        skip: 'Omitir',
        close: 'Cerrar',
        step: 'Paso {{step}} de {{total}}',
        pilotEyebrow: 'Llegaste temprano',
        pilotTitle: 'Bienvenido, Pilot Angler',
        pilotKicker: 'Acceso temprano',
        pilotDesc: 'Gracias por ayudar a darle forma a Roddo desde temprano. Tus comentarios nos ayudan a crear una mejor app de pesca para pescadores reales.',
        pilotAccent: 'Los usuarios piloto ganan beneficios tempranos.',
        startHere: 'Empieza aquí',
        todayBitePlan: 'Bite Plan de hoy',
        dailyLoop: 'Ciclo diario',
        biteDesc: 'Tu ruta rápida para explorar agua, consultar Roddo Reads, registrar lo que pasó y aprender más rápido.',
        biteAccent: 'Saber qué hacer después',
        scoutWater: 'Explora agua',
        map: 'Mapa',
        mapDesc: 'Explora agua, guarda puntos y deja que Roddo Reads te ayude a notar lo importante.',
        mapAccent: 'Leer el agua',
        readWater: 'Leer el agua',
        waterClues: 'Pistas del agua',
        fishiqDesc: 'Roddo Reads te ayuda a leer el agua con pistas locales sobre especies, hábitat, horarios y tácticas.',
        fishiqAccent: 'Mejores pistas. Menos adivinanza.',
        coreLoop: 'Ciclo principal',
        logCatch: 'Registrar captura',
        logDesc: 'Registra capturas como recuerdos y construye patrones desde lo que realmente pasó.',
        logAccent: 'Recordar qué funcionó',
        optionalSocial: 'Social opcional',
        community: 'Comunidad',
        bayDesc: 'Comparte momentos del agua, aprende de otros pescadores y mantén todo tranquilo. Opcional — siempre.',
        bayAccent: 'Momentos del agua',
        yourRecords: 'Tus registros',
        tackleBox: 'Tackle Box',
        holdDesc: 'Licencias, equipo e historial de capturas viven aquí: tu registro personal de pesca.',
        holdAccent: 'Tu base de pescador',
        bitePlan: 'Bite Plan',
        bitePlanControl: 'Abre tu ruta diaria y misiones iniciales.',
        recenter: 'Recentrar',
        recenterCopy: 'Vuelve a tu posición actual en el mapa.',
        fishiqControl: 'Abre la guía local de especies y señales.',
        favorites: 'Favoritos',
        favoritesCopy: 'Vuelve a puntos y aguas guardadas.',
        ready: 'Listo para empezar',
        startBitePlan: '¿Empezar con el Bite Plan de hoy?',
        fastestLoop: 'El ciclo más rápido de Roddo es simple: planifica, explora, consulta Roddo Reads y registra lo aprendido.',
        reopenTour: 'Puedes volver a abrir este recorrido luego en Configuración',
        openBitePlan: 'Abrir Bite Plan',
        scoutBigBlue: 'Explorar Big Blue',
        logFirstCatch: 'Registrar primera captura',
        exploreAnytime: 'Puedes explorar Big Blue o registrar tu primera captura cuando quieras.',
        tileEarlyAccess: 'Acceso temprano',
        tileBitePlan: 'Bite Plan',
        tileBigBlue: 'Big Blue',
        tileFishIQ: 'Roddo Reads',
        tileLog: "Capt'n's Log",
      },
      legal: {
        trustCenter: 'Centro de confianza',
        legalPrivacy: 'Legal y privacidad',
        legalPrivacyCopy: 'Revisa documentos de Roddo, estado de consentimiento, onboarding y preferencias de datos de pesca.',
        documents: 'Documentos',
        terms: 'Términos de Servicio',
        termsCopy: 'Reglas para usar Roddo y las funciones de comunidad.',
        privacy: 'Política de Privacidad',
        privacyCopy: 'Cómo Roddo recopila, usa y protege datos de cuenta, ubicación, registros y contenido.',
        dataPreference: 'Preferencia de datos',
        fishiqContribution: 'Contribución de datos de pesca',
        fishiqContributionCopy: 'Permite que registros y observaciones elegibles mejoren Roddo de forma agregada y anonimizada. Los datos personales del perfil no se venden.',
        contributionEnabled: 'Contribución activada',
        contributionDisabled: 'Contribución desactivada',
        contributionEnabledSentence: 'La contribución está activada actualmente.',
        contributionDisabledSentence: 'La contribución está desactivada actualmente.',
        onboarding: 'Onboarding',
        onboardingWalkthrough: 'Recorrido de onboarding',
        onboardingCopy: 'Repite el tour introductorio para repasar rápidamente lo básico de Roddo.',
        onboardingMeta: 'Esto no reinicia tu cuenta, perfil, registros ni aceptación legal.',
        openTour: 'Abrir tour',
        ageConsent: 'Edad y consentimiento',
        ageStatus: 'Estado de edad',
        minorReadonly: 'La configuración de cuenta menor actualmente es de solo lectura.',
        noMinorRestrictions: 'No hay restricciones de cuenta menor activas.',
        parentalConsent: 'Consentimiento parental',
        parentalReceived: 'recibido {{date}}',
        parentalNotReceived: 'no recibido',
        parentalNotRequired: 'No se requiere consentimiento parental.',
        legalDocument: 'Documento legal',
        reachedEnd: 'Llegaste al final.',
        scrollToAccept: 'Desplázate hasta abajo para habilitar la aceptación.',
        reacceptPrivacy: 'Volver a aceptar la Política de Privacidad',
        acceptPrivacy: 'Aceptar Política de Privacidad',
        reacceptTerms: 'Volver a aceptar los Términos',
        acceptTerms: 'Aceptar Términos',
        publicBookmark: 'Puedes guardar esta página para más tarde.',
        publicTip: 'Consejo: puedes guardar esta página.',
        backToRoddo: 'Volver a Roddo',
      },
      profile: {
        overview: 'Resumen',
        analytics: 'Estadísticas',
        level: 'Nivel',
        catches: 'Capturas',
        species: 'Especies',
        spots: 'Puntos',
        joined: 'Se unió {{date}}',
        myLicenses: 'Mis licencias de pesca',
        myCatchDiary: 'Mi Capt’n’s Log',
        noCatches: '¡Tu diario de capturas está vacío!',
        noCatchesPrompt: 'Usa “Log” para empezar tu diario.',
        editProfile: 'Editar perfil',
        publicProfile: 'Perfil público',
        follow: 'Seguir',
        following: 'Siguiendo',
        message: 'Mensaje',
        report: 'Reportar',
      },
      logCatch: {
        title: 'Cuéntame sobre tu captura',
        photoLabel: 'Foto de tu captura',
        uploadPhoto: 'Subir foto',
        speciesLabel: '¿Qué pez era?',
        speciesPlaceholder: 'ej. lobina negra',
        weightLabel: '¿Cuánto pesó?',
        weightPlaceholder: 'ej. 5.2 lb',
        date: 'Fecha',
        time: 'Hora',
        locationLabel: '¿Dónde lo capturaste?',
        locationPlaceholder: 'ej. Lake Harmony, PA',
        useCurrentLocation: 'Usar ubicación actual',
        selectOnMap: 'Seleccionar en mapa',
        lureBaitLabel: 'Señuelo / carnada usada',
        lureBaitPlaceholder: 'ej. rana topwater o lombriz',
        techniqueLabel: 'Técnica de pesca',
        techniquePlaceholder: 'ej. drop shot',
        saveButton: 'Guardar y compartir',
        saving: 'Guardando...',
        confirmLocation: 'Confirmar ubicación',
      },
      gear: {
        hero: {
          title: 'Tu equipo de pesca',
          subtitle: 'Prepara cañas, carretes, señuelos, línea y equipo inteligente antes de la próxima salida. The Hold mantiene todo organizado y listo.',
        },
        manage: 'Gestionar',
        addGear: '✨ Agregar equipo',
        totalGear: 'Equipo total',
        loadoutScore: 'Loadout',
        mostUsed: 'Más usado',
        heaviestLure: 'Señuelo más pesado',
        none: 'Nada aún',
        equipped: 'Equipado',
        equip: 'Equipar',
        activeKit: 'Kit activo',
        savedCount: '{{count}} guardado',
        editGear: 'Editar equipo',
        tabs: {
          rod: 'Cañas',
          reel: 'Carretes',
          baitsLures: 'Carnadas',
          line: 'Línea',
          smart: 'Smart',
          other: 'Otro',
        },
        categories: {
          rod: 'Caña',
          reel: 'Carrete',
          lure: 'Señuelo',
          bait: 'Carnada',
          line: 'Línea',
          smart: 'Smart',
          gear: 'Equipo',
        },
        fields: {
          name: 'Nombre',
          brand: 'Marca',
          model: 'Modelo',
          weightOz: 'Peso (oz)',
          value: 'Valor',
          notes: 'Notas',
        },
        delete: {
          title: '¿Eliminar equipo?',
          confirm: 'Esto elimina permanentemente el equipo guardado en The Hold.',
        },
      },
      gearHub: {
        title: 'The Hold',
        subtitle: 'Equipo, licencias y preparación en un solo lugar.',
        kitShelf: '🧃 Estante de equipo',
        addGear: '✨ Agregar equipo',
        empty: 'Nada aquí todavía.',
        emptyHint: 'Toca ✨ Agregar equipo para guardar la próxima pieza.',
        manageTackle: 'Gestionar Tackle Box',
        equipped: 'Equipado',
      },
      tackleBox: {
        title: 'Tackle Box',
        rods: 'Cañas',
        reels: 'Carretes',
        lures: 'Señuelos',
        baits: 'Carnadas',
        line: 'Línea',
        other: 'Otro equipo',
        addGear: 'Agregar equipo',
        noGear: 'Aún no hay equipo.',
        tabs: {
          loadout: 'Loadout',
          stats: 'Estadísticas',
          avatar: 'Avatar',
        },
        gear: {
          title: 'Equipado',
          none: 'Nada equipado',
          manage: 'Gestionar equipo',
          add: 'Agregar equipo',
          equipSuccess: 'Equipado.',
          clearSuccess: 'Equipo equipado borrado.',
        },
        avatar: {
          shellWallet: 'Billetera Shells',
          shellWalletCopy: 'Moneda cosmética disponible para equipo de avatar. Gana más registrando, usando Roddo Reads, completando retos y recibiendo recompensas.',
          lifetimeEarned: 'Total ganado',
          avatarGear: 'Equipo de avatar',
          profileLoadout: 'Loadout de perfil',
          profileLoadoutCopy: 'Gestiona cosméticos de tu Angler Card. Las compras de Shell Shop son solo cosméticas.',
          equipped: 'Equipado',
          starter: 'Inicial',
          unequip: 'Quitar',
          equip: 'Equipar',
          locked: 'Bloqueado',
          shellShopPreview: 'Vista previa de Shell Shop',
          shells: 'Shells',
          tuckedAway: 'Loadout de avatar guardado',
          hiddenSummary: '{{equipped}} equipados · {{owned}} cosméticos propios',
          tapToOpen: 'Toca para abrir',
        },
        deck: {
          label: 'Panel de equipo',
          title: 'Kit activo + inventario',
          copy: 'Equipo guardado arriba. Loadout equipado abajo.',
          activeRod: 'Caña activa',
          activeReel: 'Carrete activo',
          activeLure: 'Señuelo activo',
        },
        licenses: {
          manage: 'Gestionar licencias',
          viewPhoto: 'Ver foto',
        },
      },
      publicProfile: {
        profile: 'Perfil',
        catches: 'Capturas',
        species: 'Especies',
        spots: 'Puntos',
        posts: 'Publicaciones',
        recentPosts: 'Publicaciones recientes',
        noPosts: 'Aún no hay publicaciones públicas.',
      },
      support: {
        title: 'Soporte',
        subtitle: 'Cuéntanos qué pasa y te ayudaremos.',
      },
    },
  },
  ko: {
    translation: {
      app: {
        loading: '로딩 중...',
      },
      views: {
        Home: 'The Hold',
        Map: 'Big Blue',
        Gadgets: '기기',
        Community: 'Baleen Bay',
        Bazaar: 'Baleen Bay Bazaar',
        Events: '이벤트',
        Profile: '내 페이지',
        LogCatch: 'Capt’n’s Log',
        EditProfile: '프로필 수정',
        Challenges: '챌린지',
      },
      common: {
        save: '저장',
        cancel: '취소',
        delete: '삭제',
        edit: '수정',
        confirm: '확인',
        submit: '제출',
        search: '검색',
        close: '닫기',
        back: '뒤로',
        next: '다음',
        finish: '완료',
        skip: '지금은 건너뛰기',
        done: '완료',
        view: '보기',
        open: '열기',
        hide: '숨기기',
        buy: '구매',
        buying: '구매 중',
        copyLink: '링크 복사',
        saveChanges: '변경사항 저장',
        saved: '저장됨',
        saving: '저장 중…',
        accepted: '동의함',
        missing: '필요함',
        version: '버전',
        acceptedAt: '{{date}} 동의됨',
        notRecorded: '기록 없음',
        step: '{{total}}단계 중 {{step}}단계',
      },
      settings: {
        title: '설정',
        subtitle: '낚시 스타일에 맞게 Roddo를 조정하세요.',
        account: '계정',
        preferences: '환경설정',
        legal: '법적 고지 및 개인정보',
        support: '지원',
      },
      bottomNav: {
        home: 'The Hold',
        map: 'Big Blue',
        community: 'Baleen Bay',
        log: 'Log',
        profile: '프로필',
      },
      editProfile: {
        title: '프로필 수정',
        subtitle: 'Roddo 정체성을 깔끔하게 유지하세요.',
        language: '언어',
        languageHelp: 'Roddo의 기본 언어를 변경합니다.',
        noChanges: '변경 없음',
        saveChanges: '변경사항 저장',
      },
      onboarding: {
        validEmail: '올바른 이메일을 입력해 주세요.',
        requestFailed: '요청을 제출하지 못했습니다. 다시 시도해 주세요.',
        requestSubmitted: '요청이 제출되었습니다. 승인되면 연락드릴게요.',
        requestWatchEmail: '요청이 제출되었습니다. 이메일을 확인해 주세요.',
        licenseFields: '라이선스 항목을 모두 입력해 주세요.',
        licenseAdded: '라이선스가 추가되었습니다.',
        licenseNotConfigured: '라이선스 스캔은 아직 설정되지 않았습니다. 직접 입력할 수 있어요.',
        scanNoDetails: '정보를 추출하지 못했습니다. 더 선명한 사진을 사용하거나 직접 입력해 주세요.',
        scanFailed: '라이선스를 스캔하지 못했습니다. 직접 입력해 주세요.',
        accessGateway: '접근 게이트',
        inviteCode: '초대 코드',
        verifyInvite: '초대 확인',
        checking: '확인 중…',
        accessVerified: '초대가 확인되었습니다. 환영합니다.',
        noCode: '코드가 없나요?',
        requestAccessCopy: '접근을 요청하면 자리가 열릴 때 연락드릴게요.',
        tellUsWho: '어떤 분인지 알려주세요',
        requestAccess: '접근 요청',
        continue: '계속',
        firstRun: 'Roddo 첫 실행',
        welcomeTitle: '나만의 낚시 세계에 오신 것을 환영합니다',
        welcomeCopy: 'Roddo는 Big Blue를 살피고, Roddo Reads의 유용한 신호를 확인하고, 실제 경험을 기록하고, The Hold를 정리하며, 낚시 여정을 성장으로 바꾸도록 도와줍니다.',
        startBigBlueTitle: 'Big Blue에서 시작',
        startBigBlueCopy: '세계 지도를 열고 가까운 물, 날씨, 저장한 포인트를 확인한 뒤 계획을 정하세요.',
        fishiqGuideTitle: 'Roddo Reads를 가이드로 사용',
        fishiqGuideCopy: 'Roddo Reads는 가능성 높은 어종, 시간, 조건을 알려주는 유용한 신호입니다. 마법 같은 보장은 아니에요.',
        logHappenedTitle: '실제로 일어난 일을 기록',
        logHappenedCopy: 'Capt’n’s Log는 조과, 꽝친 날, 장비, 물 상태 메모를 XP, 획득한 Shells, 더 나은 다음 예측으로 바꿉니다.',
        simpleLoop: '간단한 루프: 물 한 곳 탐색 → Roddo Reads 확인 → 낚시 → 솔직한 메모 하나 기록 → 따뜻한 성장 획득 → 다음 출조를 더 똑똑하게.',
        showLoop: '루프 보여줘',
        inviteRequired: '계속하려면 초대가 필요합니다.',
        buildCardTitle: '낚시꾼 카드 만들기',
        buildCardCopy: '간단하게 시작하세요. Roddo가 나만의 낚시 공간처럼 느껴지기 시작합니다.',
        displayName: '표시 이름',
        displayNamePlaceholder: '예: Captain Jack',
        bio: '소개',
        optional: '선택 사항',
        bioPlaceholder: '낚시 스타일, 좋아하는 물, 목표 어종, 배우는 내용을 적어보세요...',
        licenseVaultTitle: '라이선스 보관함',
        licenseVaultCopy: '설정 단계에서는 선택 사항입니다. 나중에 빠르게 필요할 때 유용해요.',
        addLicense: 'The Hold에 라이선스 추가',
        findLicense: '라이선스 정보 찾기',
        takePhoto: '사진 찍기',
        upload: '업로드',
        readingLicense: '라이선스 사진 읽는 중...',
        state: '주/지역',
        statePlaceholder: '예: California',
        licenseNumber: '라이선스 번호',
        licenseNumberPlaceholder: '예: C1234567',
        expiryDate: '만료일',
        saveContinue: '저장하고 계속',
        bitePlanTitle: '첫 Bite Plan',
        bitePlanCopy: 'Roddo의 간단한 루프로 시작하세요. 숙제도 과장도 없이 “어디서 낚시할까?”부터 “무엇을 배웠지?”까지 이어지는 길입니다.',
        firstMove: '첫 행동: Big Blue를 탭하세요.',
        firstMoveCopy: '완벽한 조과가 없어도 시작할 수 있어요. 물 메모 하나, 꽝친 날 보고 하나, 장비 선택 하나만으로도 Roddo는 함께 학습하기 시작합니다. Shells는 획득한 성장에서만 늘어납니다.',
        enterBigBlue: 'Big Blue로 들어가기',
        nextStop: '다음 목적지는 Big Blue입니다. Shell 지갑은 성장 보상을 확인하고 싶을 때 The Hold에 있어요.',
        planBigBlueCopy: '지도에서 시작하세요. 물 한 곳을 선택하고 조건을 확인합니다.',
        planFishIQCopy: '가능성 높은 어종이나 시간 단서를 고르세요. 똑똑한 직감처럼 다루면 됩니다.',
        planLogCopy: '조과, 꽝친 날, 장비, 탁도, 미끼 또는 빠른 관찰을 저장하세요.',
        planHoldCopy: '라이선스, 장비, 아바타 정체성, Shell 지갑, 준비 상태를 한곳에 보관하세요.',
        planShellsCopy: 'Roddo 성장으로 얻습니다. 아늑한 아바타 보상이며 현금이나 마켓 돈이 아닙니다.',
        planRoddodexCopy: '실제 기록과 탐색한 어종으로 물고기 지식이 자랍니다.',
        planBayCopy: '유용한 신호를 공유하고 커뮤니티 도움이 필요할 때 질문하세요.',
      },
      welcome: {
        firstCast: 'Roddo Pilot',
        title: 'Roddo Pilot에 오신 것을 환영합니다',
        subtitle: '초기 Roddo를 함께 만들어 주셔서 감사합니다.',
        tourTitle: 'Roddo Pilot 투어',
        tourSubtitle: '빠른 여섯 단계 후 바로 낚시를 시작해요.',
        skip: '건너뛰기',
        close: '닫기',
        step: '{{total}}단계 중 {{step}}단계',
        pilotEyebrow: '초기 사용자',
        pilotTitle: 'Pilot Angler님 환영합니다',
        pilotKicker: '얼리 액세스',
        pilotDesc: '초기 Roddo를 함께 만들어 주셔서 감사합니다. 여러분의 의견은 실제 낚시꾼을 위한 더 나은 앱을 만드는 데 도움이 됩니다.',
        pilotAccent: '파일럿 사용자는 초기 혜택을 받을 수 있어요.',
        startHere: '여기서 시작',
        todayBitePlan: '오늘의 Bite Plan',
        dailyLoop: '일일 루프',
        biteDesc: '물을 살피고, Roddo Reads를 확인하고, 실제 경험을 기록하며 더 빠르게 배우는 빠른 길입니다.',
        biteAccent: '다음 행동 알기',
        scoutWater: '물 살피기',
        map: '지도',
        mapDesc: '물을 탐색하고 포인트를 저장하며 Roddo Reads가 중요한 신호를 보도록 도와줍니다.',
        mapAccent: '물 읽기',
        readWater: '물 읽기',
        waterClues: '물 단서',
        fishiqDesc: 'Roddo Reads는 어종, 서식지, 시간대, 전술에 대한 지역 단서로 물을 읽도록 도와줍니다.',
        fishiqAccent: '더 좋은 단서. 덜 추측하기.',
        coreLoop: '핵심 루프',
        logCatch: '조과 기록',
        logDesc: '조과를 추억처럼 기록하고 실제로 일어난 일에서 패턴을 만드세요.',
        logAccent: '효과 있었던 것 기억하기',
        optionalSocial: '선택형 소셜',
        community: '커뮤니티',
        bayDesc: '물가의 순간을 공유하고 다른 낚시꾼에게 배우세요. 언제나 선택 사항입니다.',
        bayAccent: '물가의 순간',
        yourRecords: '내 기록',
        tackleBox: 'Tackle Box',
        holdDesc: '라이선스, 장비, 조과 기록이 여기에 있습니다. 나만의 낚시 기록 공간입니다.',
        holdAccent: '내 낚시 홈베이스',
        bitePlan: 'Bite Plan',
        bitePlanControl: '일일 탐색 경로와 시작 미션을 엽니다.',
        recenter: '현재 위치',
        recenterCopy: '현재 지도 위치로 돌아갑니다.',
        fishiqControl: '지역 어종과 신호 가이드를 엽니다.',
        favorites: '즐겨찾기',
        favoritesCopy: '저장한 포인트와 물로 돌아갑니다.',
        ready: '시작 준비 완료',
        startBitePlan: '오늘의 Bite Plan으로 시작할까요?',
        fastestLoop: 'Roddo의 가장 빠른 루프는 간단합니다: 계획하고, 탐색하고, Roddo Reads를 확인하고, 배운 것을 기록하세요.',
        reopenTour: '이 투어는 나중에 설정에서 다시 열 수 있어요',
        openBitePlan: 'Bite Plan 열기',
        scoutBigBlue: 'Big Blue 탐색',
        logFirstCatch: '첫 조과 기록',
        exploreAnytime: 'Big Blue 탐색이나 첫 조과 기록은 언제든 할 수 있어요.',
        tileEarlyAccess: '얼리 액세스',
        tileBitePlan: 'Bite Plan',
        tileBigBlue: 'Big Blue',
        tileFishIQ: 'Roddo Reads',
        tileLog: "Capt'n's Log",
      },
      legal: {
        trustCenter: '신뢰 센터',
        legalPrivacy: '법적 고지 및 개인정보',
        legalPrivacyCopy: 'Roddo 문서, 동의 상태, 온보딩, 낚시 데이터 설정을 확인하세요.',
        documents: '문서',
        terms: '서비스 약관',
        termsCopy: 'Roddo와 커뮤니티 기능 이용 규칙입니다.',
        privacy: '개인정보 처리방침',
        privacyCopy: 'Roddo가 계정, 위치, 기록, 콘텐츠 데이터를 수집·사용·보호하는 방식입니다.',
        dataPreference: '데이터 설정',
        fishiqContribution: '낚시 데이터 기여',
        fishiqContributionCopy: '적합한 기록과 관찰이 집계·익명화된 형태로 Roddo 개선에 사용되도록 허용합니다. 개인 프로필 정보는 판매하지 않습니다.',
        contributionEnabled: '기여 활성화됨',
        contributionDisabled: '기여 비활성화됨',
        contributionEnabledSentence: '현재 기여가 활성화되어 있습니다.',
        contributionDisabledSentence: '현재 기여가 비활성화되어 있습니다.',
        onboarding: '온보딩',
        onboardingWalkthrough: '온보딩 둘러보기',
        onboardingCopy: 'Roddo 기본을 빠르게 복습하는 소개 투어를 다시 봅니다.',
        onboardingMeta: '계정, 프로필, 기록 또는 법적 동의는 초기화되지 않습니다.',
        openTour: '투어 열기',
        ageConsent: '나이 및 동의',
        ageStatus: '나이 상태',
        minorReadonly: '미성년자 계정 설정은 현재 읽기 전용입니다.',
        noMinorRestrictions: '활성화된 미성년자 계정 제한이 없습니다.',
        parentalConsent: '부모 동의',
        parentalReceived: '{{date}} 수신',
        parentalNotReceived: '미수신',
        parentalNotRequired: '부모 동의가 필요하지 않습니다.',
        legalDocument: '법적 문서',
        reachedEnd: '끝까지 도달했습니다.',
        scrollToAccept: '동의를 활성화하려면 아래까지 스크롤하세요.',
        reacceptPrivacy: '개인정보 처리방침 다시 동의',
        acceptPrivacy: '개인정보 처리방침 동의',
        reacceptTerms: '약관 다시 동의',
        acceptTerms: '약관 동의',
        publicBookmark: '이 페이지를 나중을 위해 저장할 수 있습니다.',
        publicTip: '팁: 이 페이지를 저장할 수 있습니다.',
        backToRoddo: 'Roddo로 돌아가기',
      },
      profile: {
        overview: '개요',
        analytics: '분석',
        level: '레벨',
        catches: '조과',
        species: '어종',
        spots: '포인트',
        joined: '{{date}} 가입',
        myLicenses: '내 낚시 라이선스',
        myCatchDiary: '내 Capt’n’s Log',
        noCatches: '조과 기록이 비어 있습니다!',
        noCatchesPrompt: '“Log” 버튼으로 기록을 시작하세요.',
        editProfile: '프로필 수정',
        publicProfile: '공개 프로필',
        follow: '팔로우',
        following: '팔로잉',
        message: '메시지',
        report: '신고',
      },
      logCatch: {
        title: '조과를 알려주세요',
        photoLabel: '조과 사진',
        uploadPhoto: '사진 업로드',
        speciesLabel: '어떤 물고기였나요?',
        speciesPlaceholder: '예: 큰입배스',
        weightLabel: '무게는 얼마였나요?',
        weightPlaceholder: '예: 5.2 lb',
        date: '날짜',
        time: '시간',
        locationLabel: '어디서 잡았나요?',
        locationPlaceholder: '예: Lake Harmony, PA',
        useCurrentLocation: '현재 위치 사용',
        selectOnMap: '지도에서 선택',
        lureBaitLabel: '사용한 루어 / 미끼',
        lureBaitPlaceholder: '예: 탑워터 프로그 또는 지렁이',
        techniqueLabel: '낚시 기법',
        techniquePlaceholder: '예: 드롭샷',
        saveButton: '저장하고 공유',
        saving: '저장 중...',
        confirmLocation: '위치 확인',
      },
      gear: {
        hero: {
          title: '낚시 로드아웃',
          subtitle: '다음 출조 전에 낚싯대, 릴, 루어, 라인, 스마트 장비를 정리하세요. The Hold가 장비를 깔끔하게 준비해 둡니다.',
        },
        manage: '관리',
        addGear: '✨ 장비 추가',
        totalGear: '총 장비',
        loadoutScore: '로드아웃',
        mostUsed: '가장 많이 사용',
        heaviestLure: '가장 무거운 루어',
        none: '아직 없음',
        equipped: '장착됨',
        equip: '장착',
        activeKit: '활성 키트',
        savedCount: '{{count}}개 저장됨',
        editGear: '장비 수정',
        tabs: {
          rod: '낚싯대',
          reel: '릴',
          baitsLures: '미끼',
          line: '라인',
          smart: '스마트',
          other: '기타',
        },
        categories: {
          rod: '낚싯대',
          reel: '릴',
          lure: '루어',
          bait: '미끼',
          line: '라인',
          smart: '스마트',
          gear: '장비',
        },
        fields: {
          name: '이름',
          brand: '브랜드',
          model: '모델',
          weightOz: '무게 (oz)',
          value: '가치',
          notes: '메모',
        },
        delete: {
          title: '장비를 삭제할까요?',
          confirm: 'The Hold에 저장된 장비가 영구적으로 삭제됩니다.',
        },
      },
      gearHub: {
        title: 'The Hold',
        subtitle: '장비, 라이선스, 준비 상태를 한곳에.',
        kitShelf: '🧃 장비 선반',
        addGear: '✨ 장비 추가',
        empty: '아직 아무것도 없습니다.',
        emptyHint: '✨ 장비 추가를 눌러 다음 장비를 보관하세요.',
        manageTackle: 'Tackle Box 관리',
        equipped: '장착됨',
      },
      tackleBox: {
        title: 'Tackle Box',
        rods: '낚싯대',
        reels: '릴',
        lures: '루어',
        baits: '미끼',
        line: '라인',
        other: '기타 장비',
        addGear: '장비 추가',
        noGear: '아직 장비가 없습니다.',
        tabs: {
          loadout: '로드아웃',
          stats: '통계',
          avatar: '아바타',
        },
        gear: {
          title: '장착됨',
          none: '장착된 장비 없음',
          manage: '모든 장비 관리',
          add: '장비 추가',
          equipSuccess: '장착되었습니다.',
          clearSuccess: '장착 장비를 비웠습니다.',
        },
        avatar: {
          shellWallet: 'Shell 지갑',
          shellWalletCopy: '아바타 장비에 쓰는 꾸미기 통화입니다. 기록, Roddo Reads, 챌린지, 보상으로 더 얻을 수 있어요.',
          lifetimeEarned: '누적 획득',
          avatarGear: '아바타 장비',
          profileLoadout: '프로필 로드아웃',
          profileLoadoutCopy: 'Angler Card 꾸미기 장비를 관리하세요. Shell Shop 구매는 꾸미기 전용입니다.',
          equipped: '장착됨',
          starter: '스타터',
          unequip: '해제',
          equip: '장착',
          locked: '잠김',
          shellShopPreview: 'Shell Shop 미리보기',
          shells: 'Shells',
          tuckedAway: '아바타 로드아웃 접힘',
          hiddenSummary: '{{equipped}}개 장착 · {{owned}}개 보유',
          tapToOpen: '탭해서 열기',
        },
        deck: {
          label: '장비 덱',
          title: '활성 키트 + 인벤토리',
          copy: '저장 장비는 위에, 장착 로드아웃은 아래에 있습니다.',
          activeRod: '활성 낚싯대',
          activeReel: '활성 릴',
          activeLure: '활성 루어',
        },
        licenses: {
          manage: '라이선스 관리',
          viewPhoto: '사진 보기',
        },
      },
      publicProfile: {
        profile: '프로필',
        catches: '조과',
        species: '어종',
        spots: '포인트',
        posts: '게시물',
        recentPosts: '최근 게시물',
        noPosts: '아직 공개 게시물이 없습니다.',
      },
      support: {
        title: '지원',
        subtitle: '무슨 일이 있는지 알려주시면 도와드릴게요.',
      },
    },
  }
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en',
    supportedLngs: RODDO_SUPPORTED_LANGUAGE_CODES,
    nonExplicitSupportedLngs: true,
    load: 'languageOnly',
    detection: {
      order: ['localStorage', 'htmlTag', 'navigator'],
      lookupLocalStorage: 'roddo_language',
      caches: ['localStorage'],
      excludeCacheFor: ['cimode'],
    },
    returnNull: false,
    interpolation: {
      escapeValue: false, // react already safes from xss
    },
  });

try {
  document.documentElement.lang = normalizeRoddoLanguageCode(i18n.resolvedLanguage || i18n.language);
} catch {}

export default i18n;
